import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { expenseApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const ExpenseList = () => {
  const { hasPermission } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({
    category: '',
    interval: ''
  });
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const fetchExpenses = async () => {
      try {
        setLoading(true);
        const response = await expenseApi.getAll();
        setExpenses(response.data);
        
        // Extract unique categories
        const uniqueCategories = [...new Set(response.data.map(expense => expense.category))];
        setCategories(uniqueCategories);
        
        setLoading(false);
      } catch (err) {
        setError('Failed to load expenses');
        setLoading(false);
        console.error(err);
      }
    };

    fetchExpenses();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this expense?')) {
      try {
        await expenseApi.delete(id);
        setExpenses(expenses.filter(expense => expense.id !== id));
      } catch (err) {
        setError('Failed to delete expense');
        console.error(err);
      }
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const filteredExpenses = expenses.filter(expense => {
    if (filter.category && expense.category !== filter.category) {
      return false;
    }
    if (filter.interval && expense.interval !== filter.interval) {
      return false;
    }
    return true;
  });

  if (loading) {
    return <div className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x"></i></div>;
  }

  if (error) {
    return <div className="alert alert-danger"><i className="fas fa-exclamation-triangle me-2"></i>{error}</div>;
  }

  return (
    <div className="container-fluid">
      <div className="row mb-4">
        <div className="col">
          <div className="d-flex justify-content-between align-items-center">
            <h1 className="display-5 mb-0">Expenses</h1>
            <div className="d-flex gap-2">
              {hasPermission('expense-payments', 'view') && (
                <Link to="/expense-payments" className="btn btn-primary">
                  <i className="fas fa-money-check-alt me-2"></i>
                  Expense Records
                </Link>
              )}
              {hasPermission('expenses', 'create') && (
                <Link to="/expenses/new" className="btn btn-success">
                  <i className="fas fa-plus-circle me-2"></i>
                  Add Expense
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col">
          <div className="card shadow-sm">
            <div className="card-header bg-white py-3">
              <h5 className="card-title mb-0">
                <i className="fas fa-filter me-2"></i>
                Filter Expenses
              </h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3 mb-md-0">
                  <label htmlFor="category" className="form-label">Category</label>
                  <select
                    className="form-select"
                    id="category"
                    name="category"
                    value={filter.category}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Categories</option>
                    {categories.map((category, index) => (
                      <option key={index} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label htmlFor="interval" className="form-label">Interval</label>
                  <select
                    className="form-select"
                    id="interval"
                    name="interval"
                    value={filter.interval}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Intervals</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                    <option value="one-time">One-time</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {filteredExpenses.length === 0 ? (
        <div className="row">
          <div className="col">
            <div className="alert alert-info d-flex align-items-center">
              <i className="fas fa-info-circle me-3 fs-4"></i>
              <div>
                No expenses found. Add your first expense to get started.
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col">
            <div className="card shadow-sm">
              <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
                <h5 className="card-title mb-0">
                  <i className="fas fa-file-invoice-dollar me-2"></i>
                  Expense List
                </h5>
                <span className="badge bg-primary rounded-pill px-3 py-2 fs-6">
                  {filteredExpenses.length} expenses
                </span>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead >
                      <tr>
                        <th>Name</th>
                        <th>Category</th>
                        <th>Interval</th>
                        <th>Monthly Split</th>
                        <th className="text-end" style={{ width: '1%', whiteSpace: 'nowrap' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredExpenses.map(expense => (
                        <tr key={expense.id}>
                          <td>
                            <Link to={`/expenses/${expense.id}`} className="text-decoration-none fw-bold">
                              {expense.name}
                            </Link>
                          </td>
                          <td>
                            <span className="badge bg-secondary rounded-pill">
                              {expense.category}
                            </span>
                          </td>
                          <td>
                            <span className={`badge ${
                              expense.interval === 'monthly' ? 'bg-primary' : 
                              expense.interval === 'quarterly' ? 'bg-info' : 
                              expense.interval === 'yearly' ? 'bg-warning text-dark' : 
                              'bg-dark'
                            }`}>
                              {expense.interval}
                            </span>
                          </td>
                          <td>
                            {expense.monthly_split ? 
                              <span className="badge bg-success">Yes</span> : 
                              <span className="badge bg-light text-dark">No</span>
                            }
                          </td>
                          <td>
                            <div className="btn-group float-end">
                              <Link to={`/expenses/${expense.id}`} className="btn btn-sm btn-outline-primary" title="View">
                                <i className="fas fa-eye"></i>
                              </Link>
                              {hasPermission('expenses', 'update') && (
                                <Link to={`/expenses/${expense.id}/edit`} className="btn btn-sm btn-outline-secondary" title="Edit">
                                  <i className="fas fa-edit"></i>
                                </Link>
                              )}
                              {hasPermission('expenses', 'delete') && (
                                <button 
                                  onClick={() => handleDelete(expense.id)} 
                                  className="btn btn-sm btn-outline-danger"
                                  title="Delete"
                                >
                                  <i className="fas fa-trash-alt"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseList;
